import { Dialog, DialogContent, DialogHeader, DialogTitle } from "@/components/ui/dialog";
import { Progress } from "@/components/ui/progress";
import { FileIcon, Upload, X, CheckCircle, AlertCircle } from "lucide-react";
import { Button } from "@/components/ui/button";

interface UploadProgressDialogProps {
  open: boolean;
  files: Array<{
    name: string;
    size: number;
    progress: number;
    status: 'waiting' | 'uploading' | 'completed' | 'error' | 'cancelled';
  }>;
  totalProgress: number;
  onCancel?: (fileIndex: number) => void;
  onCancelAll?: () => void;
}

export function UploadProgressDialog({ 
  open, 
  files, 
  totalProgress, 
  onCancel, 
  onCancelAll 
}: UploadProgressDialogProps) {
  const formatFileSize = (bytes: number): string => {
    if (bytes < 1024) return bytes + ' B';
    if (bytes < 1048576) return (bytes / 1024).toFixed(1) + ' KB';
    return (bytes / 1048576).toFixed(1) + ' MB';
  };

  const hasActiveUploads = files.some(f => f.status === 'uploading');

  return (
    <Dialog open={open}>
      <DialogContent className="max-w-[90vw] sm:max-w-[500px]">
        <DialogHeader>
          <div className="flex items-center justify-between">
            <DialogTitle className="flex items-center gap-2">
              <Upload className="h-5 w-5" />
              Uploading Files
            </DialogTitle>
            {hasActiveUploads && onCancelAll && (
              <Button
                variant="ghost"
                size="sm"
                onClick={onCancelAll}
                className="text-destructive hover:text-destructive/80"
              >
                Cancel All
              </Button>
            )}
          </div>
        </DialogHeader>
        <div className="space-y-4 max-w-full">
          <div className="space-y-3">
            {files.map((file, index) => (
              <div key={index} className="space-y-2">
                <div className="flex items-center justify-between text-sm gap-2">
                  <div className="flex items-center gap-2 flex-1 min-w-0">
                    <FileIcon className="h-4 w-4 flex-shrink-0" />
                    <div className="flex-1 min-w-0 flex items-center gap-1">
                      <span className="truncate block max-w-[200px]" title={file.name}>
                        {file.name}
                      </span>
                      <span className="text-muted-foreground text-xs flex-shrink-0">
                        ({formatFileSize(file.size)})
                      </span>
                    </div>
                  </div>
                  <div className="flex items-center gap-2 flex-shrink-0">
                    <span className="text-xs text-muted-foreground">
                      {file.status === 'completed' ? 'Complete' : 
                       file.status === 'error' ? 'Failed' : 
                       file.status === 'cancelled' ? 'Cancelled' :
                       file.status === 'uploading' ? `${file.progress}%` : 
                       'Waiting...'}
                    </span>
                    {file.status === 'completed' && (
                      <CheckCircle className="h-4 w-4 text-green-500" />
                    )}
                    {file.status === 'error' && (
                      <AlertCircle className="h-4 w-4 text-destructive" />
                    )}
                    {file.status === 'uploading' && onCancel && (
                      <button
                        onClick={() => onCancel(index)}
                        className="text-muted-foreground hover:text-destructive transition-colors p-1"
                        title="Cancel this upload"
                      >
                        <X className="h-4 w-4" />
                      </button>
                    )}
                  </div>
                </div>
                <Progress 
                  value={file.progress} 
                  className={`h-2 w-full ${
                    file.status === 'error' ? 'bg-destructive/20' : 
                    file.status === 'cancelled' ? 'bg-muted' : ''
                  }`}
                />
              </div>
            ))}
          </div>
          
          <div className="border-t pt-3">
            <div className="flex justify-between text-sm font-medium mb-2">
              <span>Total Progress</span>
              <span>{Math.round(totalProgress)}%</span>
            </div>
            <Progress value={totalProgress} className="h-3" />
          </div>
        </div>
      </DialogContent>
    </Dialog>
  );
}